import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'
import PptxGenJS from 'pptxgenjs'

const SLIDE_BG = '#eef3f6'
const WIDE_MM = [338.67, 190.5] // 16:9 — 13.333" × 7.5"
const PDF_MARGIN_MM = 14
const PPT_W = 13.333
const PPT_H = 7.5
const PPT_MARGIN = 0.5

function fitContain(srcW, srcH, boxW, boxH) {
  const scale = Math.min(boxW / srcW, boxH / srcH)
  const w = srcW * scale
  const h = srcH * scale
  return {
    w,
    h,
    x: (boxW - w) / 2,
    y: (boxH - h) / 2,
  }
}

async function captureNode(element) {
  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    backgroundColor: SLIDE_BG,
    windowWidth: Math.max(element.scrollWidth, 1200),
    logging: false,
    onclone: (clonedDoc) => {
      clonedDoc.querySelectorAll('.no-pdf').forEach((el) => {
        el.style.display = 'none'
      })
      clonedDoc.querySelectorAll('.pack-slide').forEach((el) => {
        el.style.display = 'block'
        el.style.position = 'relative'
        el.style.left = 'auto'
        el.style.top = 'auto'
        el.style.visibility = 'visible'
        el.style.pointerEvents = 'auto'
        el.style.marginBottom = '0'
        el.style.width = '1280px'
        el.style.maxWidth = '1280px'
      })
    },
  })
  return {
    dataUrl: canvas.toDataURL('image/jpeg', 0.93),
    width: canvas.width,
    height: canvas.height,
  }
}

/**
 * Capture each [data-slide] node as its own image. Falls back to the root.
 */
export async function captureSlides(root) {
  if (!root) throw new Error('Nothing to export')
  const nodes = [...root.querySelectorAll('[data-slide]')]
  const targets = nodes.length ? nodes : [root]
  const slides = []
  for (const el of targets) {
    const img = await captureNode(el)
    slides.push({
      title: el.getAttribute('data-slide') || 'Board pack',
      ...img,
    })
  }
  return slides
}

export async function exportSlidesToPdf(slides, {
  filename = 'board-pack.pdf',
  title = 'Board AML/CTF Report',
} = {}) {
  if (!slides?.length) throw new Error('Nothing to export')
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: WIDE_MM, compress: true })
  const pageW = pdf.internal.pageSize.getWidth()
  const pageH = pdf.internal.pageSize.getHeight()
  const boxW = pageW - PDF_MARGIN_MM * 2
  const boxH = pageH - PDF_MARGIN_MM * 2 - 6

  slides.forEach((slide, i) => {
    if (i > 0) pdf.addPage()
    pdf.setFillColor(238, 243, 246)
    pdf.rect(0, 0, pageW, pageH, 'F')
    const fit = fitContain(slide.width, slide.height, boxW, boxH)
    pdf.addImage(
      slide.dataUrl,
      'JPEG',
      PDF_MARGIN_MM + fit.x,
      PDF_MARGIN_MM + fit.y,
      fit.w,
      fit.h,
      undefined,
      'FAST',
    )
    pdf.setFontSize(8)
    pdf.setTextColor(100, 116, 139)
    pdf.text(
      `Confidential — Board  ·  ${title}  ·  ${i + 1} / ${slides.length}`,
      pageW / 2,
      pageH - 7,
      { align: 'center' },
    )
  })
  pdf.save(filename)
}

export async function exportSlidesToPptx(slides, {
  filename = 'board-pack.pptx',
  title = 'Board AML/CTF Report',
} = {}) {
  if (!slides?.length) throw new Error('Nothing to export')
  const pptx = new PptxGenJS()
  pptx.author = 'PaySafeGO'
  pptx.title = title
  pptx.subject = 'Confidential board pack'
  pptx.layout = 'LAYOUT_WIDE'
  const boxW = PPT_W - PPT_MARGIN * 2
  const boxH = PPT_H - PPT_MARGIN * 2 - 0.28

  slides.forEach((slide, i) => {
    const s = pptx.addSlide()
    s.background = { color: 'EEF3F6' }
    const fit = fitContain(slide.width, slide.height, boxW, boxH)
    s.addImage({
      data: slide.dataUrl,
      x: PPT_MARGIN + fit.x,
      y: PPT_MARGIN + fit.y,
      w: fit.w,
      h: fit.h,
      sizing: { type: 'contain', w: fit.w, h: fit.h },
    })
    s.addText(
      `Confidential — Board  ·  ${title}  ·  ${i + 1} / ${slides.length}`,
      {
        x: PPT_MARGIN,
        y: PPT_H - 0.32,
        w: PPT_W - PPT_MARGIN * 2,
        h: 0.24,
        fontSize: 10,
        color: '64748B',
        align: 'center',
        fontFace: 'Calibri',
      },
    )
  })
  await pptx.writeFile({ fileName: filename })
}

/**
 * Capture a DOM node and download it as a multi-page PDF (not the print dialog).
 * Prefer exportSlidesToPdf for board packs.
 */
export async function exportElementToPdf(element, {
  filename = 'export.pdf',
  title = 'Board AML/CTF Report',
} = {}) {
  const slides = await captureSlides(element)
  await exportSlidesToPdf(slides, { filename, title })
}
